import React, { Component } from "react";

import classnames from "classnames";
import isIntuit from "Intuit/isIntuit";
import PropTypes from "prop-types";
import SIReactOnRails from "Utils/ReactOnRails";
import { isNotDot, isNotFlat, isNotNarrowVertical, validateLongestStrokeLength } from "Utils/signatureValidations";

import { getBreakpoint, getWindowSize } from "Common/Responsive";
import { Body2 as BodyText } from "Theme/BodyText";
import Button from "Theme/Button";

import SignPad from "./SignPad";

import makeBem from "Utils/makeBem";

const bem = makeBem("SignatureForm");
export default class SignatureForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      signed: props.imageDataURL,
      showSignatureErrors: false,
    };
    this.signatureRef = React.createRef();
  }

  render() {
    /* eslint-disable no-unused-vars */
    const { clearButton, showSubmitButton, showClearButton, submitButtonText, clearButtonText, className, alignment, theme, input, meta, responsive, ...rest } =
      this.props;

    // Set the width and height of the signature pad based on the breakpoint
    // this is set manually because canvas requites a specific width and height
    // these numbers account for the width of padding and border on the signature pad
    // as well as the widths and breakpoints of the form
    let signPadWidth;
    let signPadHeight;

    if (responsive) {
      const breakpoint = getBreakpoint();
      if (breakpoint === "lg") {
        signPadWidth = 358;
        signPadHeight = 119;
      } else if (breakpoint === "md") {
        signPadWidth = 350;
        signPadHeight = 116;
      } else if (breakpoint === "sm") {
        signPadWidth = 372;
        signPadHeight = 124;
      } else {
        const windowWidth = window.innerWidth;
        const outsidePadding = (24 + 16) * 2; // 16px outer div 24px inner div
        const signaturePadPaddingBorder = (8 + 1) * 2; // 8px padding 1px border
        const usableWidth = windowWidth - (outsidePadding + signaturePadPaddingBorder);

        signPadWidth = usableWidth;
        signPadHeight = usableWidth / 3;
      }
    } else {
      signPadWidth = 300;
      signPadHeight = 100;
    }

    /* eslint-enable no-unused-vars */
    const classes = classnames("SignatureForm", `SignatureForm--align-${alignment}`);
    return (
      <div className={classes}>
        <SignPad
          className="SignatureForm__signature"
          ref={this.signatureRef}
          {...input}
          onChange={this.handleOnChange.bind(this)}
          {...rest}
          onBegin={this.handleBeginSignature.bind(this)}
          onEnd={this.handleEndSignature.bind(this)}
          width={signPadWidth}
          height={signPadHeight}
        />
        {this.renderButtonRow()}
        {this.state.showSignatureErrors ? this.renderSignatureErrors() : this.renderBlankSpace()}
      </div>
    );
  }

  handleOnChange(value) {
    if (this.validateSignature() || value === null) {
      this.props.input.onChange(value);
    }
  }

  renderBlankSpace() {
    return (
      <BodyText className={classnames(bem("Validation"), bem("Validation", "error"))} error mt={2}>
        &nbsp;
      </BodyText>
    );
  }

  renderSignatureErrors() {
    return (
      <BodyText className={classnames(bem("Validation"), bem("Validation", "error"))} error mt={2}>
        Invalid signature. Please sign your name or initials
      </BodyText>
    );
  }

  renderClearButton() {
    if (this.props.clearButton) {
      return <div onClick={this.handleClear.bind(this)}>{this.props.clearButton}</div>;
    }

    return (
      <Button color={isIntuit() ? "link" : "gray"} onClick={this.handleClear.bind(this)}>
        {this.props.clearButtonText}
      </Button>
    );
  }

  renderButtonRow() {
    const showButtonRow = this.props.showSubmitButton || this.props.showClearButton;
    return (
      showButtonRow && (
        <div className="btn-group clearfix SignatureForm__buttons">
          {this.props.showSubmitButton && (
            <Button disabled={!this.state.signed} onClick={this.handleSubmit.bind(this)} type="submit">
              {this.props.submitButtonText}
            </Button>
          )}
          {this.props.showClearButton && this.renderClearButton()}
        </div>
      )
    );
  }

  validateSignature() {
    // component hasn't loaded yet
    if (!this.signatureRef.current) {
      return;
    }

    // Validate that the signature is more than just a bunch of dots
    // or a single horizontal/vertical line
    let data = this.signatureRef.current.signaturePad.toData();
    // include all strokes in the validated set of data
    data = [data.reduce((a, b) => a.concat(b), [])];
    return isNotDot(data) && validateLongestStrokeLength(data) && isNotFlat(data) && isNotNarrowVertical(data);
  }

  handleBeginSignature() {}

  handleEndSignature() {
    if (this.validateSignature()) {
      this.setState({
        signed: true,
        showSignatureErrors: false,
      });
    } else {
      this.setState({
        showSignatureErrors: true,
      });
    }
  }

  handleClear() {
    this.signatureRef.current.clear();
    this.setState({
      signed: false,
      showSignatureErrors: true,
    });

    if (this.props.onClear) {
      this.props.onClear.call(null);
    }
  }

  handleSubmit(e) {
    if (!this.state.signed) {
      e.preventDefault();
    }
  }
}
SignatureForm.propTypes = {
  name: PropTypes.string.isRequired,
  imageDataURL: PropTypes.string,
  showSubmitButton: PropTypes.bool,
  showClearButton: PropTypes.bool,
  clearButtonText: PropTypes.string,
  submitButtonText: PropTypes.string,
  responsive: PropTypes.bool,
};
SignatureForm.defaultProps = {
  name: "signature",
  showSubmitButton: true,
  showClearButton: true,
  clearButtonText: "Clear",
  submitButtonText: "Click to Sign",
  alignment: "left",
  responsive: false,
};
SIReactOnRails.register(
  {
    SignatureForm,
  },
  module,
);
